import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Activity, FileCheck2, ScanLine, Gavel, MessageSquareText } from "lucide-react";
import { useI18n } from "../../i18n";
import { api, apiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const SERVICES = [
  ["monthly_health_check", Activity, { en: "Monthly VAT Health Check", ar: "الفحص الشهري لصحة الضريبة" }, { en: "Monthly analytics + specialist review of sales, purchases and the VAT return.", ar: "تحليلات شهرية ومراجعة المختص للمبيعات والمشتريات والإقرار." }],
  ["vat_return_review", FileCheck2, { en: "VAT Return Review", ar: "مراجعة الإقرار الضريبي" }, { en: "Reconciliation and review before you approve for filing.", ar: "تسوية ومراجعة قبل اعتمادك للتقديم." }],
  ["document_checker", ScanLine, { en: "VAT Document Checker", ar: "فاحص المستندات" }, { en: "AI classifies an invoice and extracts VAT fields.", ar: "الذكاء الاصطناعي يصنّف الفاتورة ويستخرج الحقول." }],
  ["zatca_audit", Gavel, { en: "ZATCA Audit Support", ar: "دعم فحص الهيئة" }, { en: "Upload a ZATCA notice and manage the full response workflow.", ar: "ارفع إشعار الهيئة وأدر سير الرد الكامل." }],
  ["advisory", MessageSquareText, { en: "Ask a VAT Specialist", ar: "اسأل مختص ضريبي" }, { en: "Submit a question and receive a reviewed advisory note.", ar: "أرسل سؤالاً واحصل على مذكرة استشارية مراجعة." }],
];

export default function NewRequest() {
  const { t, lang } = useI18n();
  const nav = useNavigate();
  const [type, setType] = useState(null);
  const [f, setF] = useState({ title: "", description: "", question: "", amount: "", counterparty: "", country: "", notice_type: "", reference_number: "", priority: "normal" });
  const [loading, setLoading] = useState(false);
  const [fw, setFw] = useState({});
  useEffect(() => { api.get("/service-framework").then((r) => setFw(r.data)).catch(() => {}); }, []);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const submit = async () => {
    setLoading(true);
    try {
      const payload = { service_type: type, ...f, amount: f.amount ? parseFloat(f.amount) : null };
      const { data } = await api.post("/cases", payload);
      toast.success(data.reference);
      nav(`/app/cases/${data.id}`);
    } catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
  };

  return (
    <div className="max-w-3xl mx-auto space-y-6 gc-fade-up">
      <div>
        <h1 className="font-display text-2xl font-bold text-[hsl(217_63%_15%)]">{t("new_request_title")}</h1>
        <p className="text-muted-foreground mt-1">{t("new_request_sub")}</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-4">
        {SERVICES.map(([id, Icon, name, desc]) => (
          <button key={id} data-testid={`service-${id}`} onClick={() => setType(id)}
            className={`text-start rounded-2xl border p-5 transition-colors ${type === id ? "border-[hsl(158_64%_28%)] ring-1 ring-[hsl(158_64%_28%)] bg-[hsl(158_64%_98%)]" : "border-border bg-card hover:border-[hsl(158_64%_28%)]"}`}>
            <div className="h-10 w-10 rounded-xl bg-[hsl(217_63%_15%)] flex items-center justify-center"><Icon className="h-5 w-5 text-white" /></div>
            <div className="mt-3 font-semibold text-[hsl(217_63%_15%)]">{name[lang]}</div>
            <div className="text-xs text-muted-foreground mt-1">{desc[lang]}</div>
          </button>
        ))}
      </div>

      {type && fw[type] && (
        <div className="rounded-2xl border border-border bg-secondary/40 p-5 grid sm:grid-cols-3 gap-4 gc-fade-up" data-testid="service-framework">
          {[["fw_inputs", "inputs"], ["fw_measures", "measures"], ["fw_outputs", "outputs"]].map(([lbl, key]) => (
            <div key={key}>
              <div className="text-xs font-semibold text-[hsl(158_64%_28%)] mb-2">{t(lbl)}</div>
              <ul className="space-y-1 text-xs text-muted-foreground">{(fw[type][key] || []).map((x, i) => <li key={i}>• {lang === "ar" ? x.ar : x.en}</li>)}</ul>
            </div>
          ))}
        </div>
      )}

      {type && (
        <div className="rounded-2xl border border-border bg-card p-6 space-y-4 gc-fade-up">
          <div><Label>{t("title_field")}</Label><Input data-testid="req-title" className="mt-1.5" value={f.title} onChange={set("title")} /></div>
          {type === "advisory" ? (
            <>
              <div><Label>{t("question_field")}</Label><Textarea data-testid="req-question" className="mt-1.5" rows={4} value={f.question} onChange={set("question")} /></div>
              <div className="grid sm:grid-cols-3 gap-3">
                <div><Label>{t("amount_field")}</Label><Input data-testid="req-amount" type="number" className="mt-1.5" value={f.amount} onChange={set("amount")} /></div>
                <div><Label>{t("counterparty_field")}</Label><Input data-testid="req-counterparty" className="mt-1.5" value={f.counterparty} onChange={set("counterparty")} /></div>
                <div><Label>{t("country_field")}</Label><Input data-testid="req-country" className="mt-1.5" value={f.country} onChange={set("country")} /></div>
              </div>
            </>
          ) : type === "zatca_audit" ? (
            <>
              <div className="grid sm:grid-cols-2 gap-3">
                <div><Label>{t("notice_type_field")}</Label><Input data-testid="req-notice-type" className="mt-1.5" value={f.notice_type} onChange={set("notice_type")} /></div>
                <div><Label>{t("ref_number_field")}</Label><Input data-testid="req-ref-number" className="mt-1.5" value={f.reference_number} onChange={set("reference_number")} /></div>
              </div>
              <div><Label>{t("description")}</Label><Textarea data-testid="req-desc" className="mt-1.5" rows={3} value={f.description} onChange={set("description")} /></div>
            </>
          ) : (
            <div><Label>{t("description")}</Label><Textarea data-testid="req-desc" className="mt-1.5" rows={3} value={f.description} onChange={set("description")} /></div>
          )}
          <Button data-testid="req-submit" disabled={loading} onClick={submit} className="w-full rounded-full h-11 bg-[hsl(217_63%_15%)] hover:bg-[hsl(217_63%_20%)]">{loading ? t("loading") : t("submit_request")}</Button>
          <p className="text-xs text-muted-foreground">{t("disclaimer_note")}</p>
        </div>
      )}
    </div>
  );
}
